import React, { useState, useEffect } from 'react';
import { MappingPresetTemplate, FieldMappingRule, AutomationMappingPreset, WebhookTemplate, WebhookTemplateGroup } from '../types/automation';
import { api } from '../lib/api';

interface AutomationPresetPickerProps {
  onPresetSelect: (preset: MappingPresetTemplate) => void;
  onDatabasePresetSelect?: (presetId: string) => void;
  onCustomMapping?: (mapping: Record<string, string>) => void;
  selectedPreset?: MappingPresetTemplate | null;
  selectedDatabasePresetId?: string | null;
  customMapping?: Record<string, string>;
  tenantPresets?: AutomationMappingPreset[];
}

const AutomationPresetPicker: React.FC<AutomationPresetPickerProps> = ({
  onPresetSelect,
  onDatabasePresetSelect,
  onCustomMapping,
  selectedPreset,
  selectedDatabasePresetId,
  customMapping = {},
  tenantPresets = []
}) => {
  const [presets, setPresets] = useState<MappingPresetTemplate[]>([]);
  const [webhookTemplates, setWebhookTemplates] = useState<WebhookTemplateGroup[]>([]);
  const [selectedNiche, setSelectedNiche] = useState<string>('');
  const [showCustomMapping, setShowCustomMapping] = useState<boolean>(false);
  const [customMappings, setCustomMappings] = useState<Record<string, string>>(customMapping);
  // Initialize useCustomMapping: true if customMapping has values, false if database preset is selected
  const [useCustomMapping, setUseCustomMapping] = useState<boolean>(
    Object.keys(customMapping).length > 0 && !selectedDatabasePresetId
  );

  // System presets for different niches
  const systemPresets: MappingPresetTemplate[] = [
    {
      name: "Dental Practice - Basic",
      description: "Basic field mapping for dental practice management systems",
      niche: "dental",
      field_mappings: [
        { source_field: "patient_email", target_field: "email", required: true },
        { source_field: "patient_phone", target_field: "phone" },
        { source_field: "first_name", target_field: "first_name", required: true },
        { source_field: "last_name", target_field: "last_name", required: true },
        { source_field: "appointment_date", target_field: "custom_fields.appointment_date" },
        { source_field: "service_type", target_field: "custom_fields.service_type" },
        { source_field: "provider_name", target_field: "custom_fields.provider_name" },
        { source_field: "insurance_provider", target_field: "custom_fields.insurance_provider" },
        { source_field: "patient_id", target_field: "custom_fields.patient_id" },
      ],
      required_fields: ["email", "first_name", "last_name"],
      optional_fields: ["phone", "appointment_date", "service_type", "provider_name", "insurance_provider", "patient_id"],
      validation_rules: {
        email_format: true,
        phone_format: "us"
      },
      sample_data: {
        patient_email: "john.doe@example.com",
        patient_phone: "+1234567890",
        first_name: "John",
        last_name: "Doe",
        appointment_date: "2024-01-15",
        service_type: "Cleaning",
        provider_name: "Dr. Smith",
        insurance_provider: "Blue Cross",
        patient_id: "PAT123456"
      }
    },
    {
      name: "Salon/Spa - Basic",
      description: "Basic field mapping for salon and spa management systems",
      niche: "salon",
      field_mappings: [
        { source_field: "client_email", target_field: "email", required: true },
        { source_field: "client_phone", target_field: "phone" },
        { source_field: "first_name", target_field: "first_name", required: true },
        { source_field: "last_name", target_field: "last_name", required: true },
        { source_field: "appointment_date", target_field: "custom_fields.appointment_date" },
        { source_field: "service_name", target_field: "custom_fields.service_name" },
        { source_field: "stylist_name", target_field: "custom_fields.stylist_name" },
        { source_field: "total_amount", target_field: "custom_fields.total_amount" },
        { source_field: "client_id", target_field: "custom_fields.client_id" },
        { source_field: "notes", target_field: "custom_fields.notes" },
      ],
      required_fields: ["email", "first_name", "last_name"],
      optional_fields: ["phone", "appointment_date", "service_name", "stylist_name", "total_amount", "client_id", "notes"],
      validation_rules: {
        email_format: true,
        phone_format: "us"
      },
      sample_data: {
        client_email: "jane.smith@example.com",
        client_phone: "+1234567890",
        first_name: "Jane",
        last_name: "Smith",
        appointment_date: "2024-01-15",
        service_name: "Haircut & Color",
        stylist_name: "Sarah Johnson",
        total_amount: "150.00",
        client_id: "CLI789012",
        notes: "First time client, wants highlights"
      }
    },
    {
      name: "Restaurant - Basic",
      description: "Basic field mapping for restaurant reservation systems",
      niche: "restaurant",
      field_mappings: [
        { source_field: "customer_email", target_field: "email", required: true },
        { source_field: "customer_phone", target_field: "phone" },
        { source_field: "first_name", target_field: "first_name", required: true },
        { source_field: "last_name", target_field: "last_name", required: true },
        { source_field: "reservation_date", target_field: "custom_fields.reservation_date" },
        { source_field: "party_size", target_field: "custom_fields.party_size" },
        { source_field: "special_requests", target_field: "custom_fields.special_requests" },
        { source_field: "table_preference", target_field: "custom_fields.table_preference" },
        { source_field: "customer_id", target_field: "custom_fields.customer_id" },
      ],
      required_fields: ["email", "first_name", "last_name"],
      optional_fields: ["phone", "reservation_date", "party_size", "special_requests", "table_preference", "customer_id"],
      validation_rules: {
        email_format: true,
        phone_format: "us"
      },
      sample_data: {
        customer_email: "mike.wilson@example.com",
        customer_phone: "+1234567890",
        first_name: "Mike",
        last_name: "Wilson",
        reservation_date: "2024-01-15 19:00",
        party_size: "4",
        special_requests: "Birthday celebration",
        table_preference: "Window table",
        customer_id: "CUST345678"
      }
    },
    {
      name: "Veterinary Practice - Basic",
      description: "Basic field mapping for veterinary practice management systems",
      niche: "veterinary",
      field_mappings: [
        { source_field: "owner_email", target_field: "email", required: true },
        { source_field: "owner_phone", target_field: "phone" },
        { source_field: "first_name", target_field: "first_name", required: true },
        { source_field: "last_name", target_field: "last_name", required: true },
        { source_field: "pet_name", target_field: "custom_fields.pet_name" },
        { source_field: "pet_species", target_field: "custom_fields.pet_species" },
        { source_field: "pet_breed", target_field: "custom_fields.pet_breed" },
        { source_field: "appointment_date", target_field: "custom_fields.appointment_date" },
        { source_field: "procedure_name", target_field: "custom_fields.procedure_name" },
        { source_field: "doctor_name", target_field: "custom_fields.doctor_name" },
        { source_field: "client_id", target_field: "custom_fields.client_id" },
      ],
      required_fields: ["email", "first_name", "last_name"],
      optional_fields: ["phone", "pet_name", "pet_species", "pet_breed", "appointment_date", "procedure_name", "doctor_name", "client_id"],
      validation_rules: {
        email_format: true,
        phone_format: "us"
      },
      sample_data: {
        owner_email: "jennifer.brown@example.com",
        owner_phone: "+1234567890",
        first_name: "Jennifer",
        last_name: "Brown",
        pet_name: "Buddy",
        pet_species: "Dog",
        pet_breed: "Golden Retriever",
        appointment_date: "2024-01-15",
        procedure_name: "Dental Surgery",
        doctor_name: "Dr. Smith",
        client_id: "CLI123456"
      }
    },
    {
      name: "Medical Practice - Basic",
      description: "Basic field mapping for medical practice management systems",
      niche: "medical",
      field_mappings: [
        { source_field: "patient_email", target_field: "email", required: true },
        { source_field: "patient_phone", target_field: "phone" },
        { source_field: "first_name", target_field: "first_name", required: true },
        { source_field: "last_name", target_field: "last_name", required: true },
        { source_field: "appointment_date", target_field: "custom_fields.appointment_date" },
        { source_field: "service_type", target_field: "custom_fields.service_type" },
        { source_field: "provider_name", target_field: "custom_fields.provider_name" },
        { source_field: "insurance_provider", target_field: "custom_fields.insurance_provider" },
        { source_field: "patient_id", target_field: "custom_fields.patient_id" },
        { source_field: "date_of_birth", target_field: "custom_fields.date_of_birth" },
      ],
      required_fields: ["email", "first_name", "last_name"],
      optional_fields: ["phone", "appointment_date", "service_type", "provider_name", "insurance_provider", "patient_id", "date_of_birth"],
      validation_rules: {
        email_format: true,
        phone_format: "us"
      },
      sample_data: {
        patient_email: "john.doe@example.com",
        patient_phone: "+1234567890",
        first_name: "John",
        last_name: "Doe",
        appointment_date: "2024-01-15",
        service_type: "Annual Checkup",
        provider_name: "Dr. Johnson",
        insurance_provider: "Blue Cross",
        patient_id: "PAT789012",
        date_of_birth: "1980-05-15"
      }
    },
    {
      name: "General Business - Basic",
      description: "Basic field mapping for general business customer management",
      niche: "general",
      field_mappings: [
        { source_field: "email", target_field: "email", required: true },
        { source_field: "phone", target_field: "phone" },
        { source_field: "first_name", target_field: "first_name", required: true },
        { source_field: "last_name", target_field: "last_name", required: true },
        { source_field: "company", target_field: "company" },
        { source_field: "job_title", target_field: "custom_fields.job_title" },
        { source_field: "industry", target_field: "custom_fields.industry" },
        { source_field: "lead_source", target_field: "custom_fields.lead_source" },
      ],
      required_fields: ["email", "first_name", "last_name"],
      optional_fields: ["phone", "company", "job_title", "industry", "lead_source"],
      validation_rules: {
        email_format: true,
        phone_format: "us"
      },
      sample_data: {
        email: "contact@example.com",
        phone: "+1234567890",
        first_name: "Jane",
        last_name: "Smith",
        company: "Example Corp",
        job_title: "Marketing Manager",
        industry: "Technology",
        lead_source: "Website"
      }
    },
    {
      name: "E-commerce - Basic",
      description: "Basic field mapping for e-commerce customer management",
      niche: "ecommerce",
      field_mappings: [
        { source_field: "email", target_field: "email", required: true },
        { source_field: "phone", target_field: "phone" },
        { source_field: "first_name", target_field: "first_name", required: true },
        { source_field: "last_name", target_field: "last_name", required: true },
        { source_field: "company", target_field: "company" },
        { source_field: "order_id", target_field: "custom_fields.order_id" },
        { source_field: "order_date", target_field: "custom_fields.order_date" },
        { source_field: "order_total", target_field: "custom_fields.order_total" },
        { source_field: "product_name", target_field: "custom_fields.product_name" },
      ],
      required_fields: ["email", "first_name", "last_name"],
      optional_fields: ["phone", "company", "order_id", "order_date", "order_total", "product_name"],
      validation_rules: {
        email_format: true,
        phone_format: "us"
      },
      sample_data: {
        email: "customer@example.com",
        phone: "+1234567890",
        first_name: "Mike",
        last_name: "Wilson",
        company: "Retail Store",
        order_id: "ORD123456",
        order_date: "2024-01-15",
        order_total: "99.99",
        product_name: "Product XYZ"
      }
    },
    {
      name: "Service Business - Basic",
      description: "Basic field mapping for service-based businesses",
      niche: "service",
      field_mappings: [
        { source_field: "email", target_field: "email", required: true },
        { source_field: "phone", target_field: "phone" },
        { source_field: "first_name", target_field: "first_name", required: true },
        { source_field: "last_name", target_field: "last_name", required: true },
        { source_field: "company", target_field: "company" },
        { source_field: "service_date", target_field: "custom_fields.service_date" },
        { source_field: "service_type", target_field: "custom_fields.service_type" },
        { source_field: "service_provider", target_field: "custom_fields.service_provider" },
        { source_field: "client_id", target_field: "custom_fields.client_id" },
      ],
      required_fields: ["email", "first_name", "last_name"],
      optional_fields: ["phone", "company", "service_date", "service_type", "service_provider", "client_id"],
      validation_rules: {
        email_format: true,
        phone_format: "us"
      },
      sample_data: {
        email: "client@example.com",
        phone: "+1234567890",
        first_name: "Sarah",
        last_name: "Johnson",
        company: "Business Inc",
        service_date: "2024-01-15",
        service_type: "Consulting",
        service_provider: "John Consultant",
        client_id: "CLI456789"
      }
    }
  ];

  // Convert webhook template to MappingPresetTemplate format
  const convertWebhookTemplateToPreset = (template: WebhookTemplate): MappingPresetTemplate => {
    // Extract required fields from field mappings (fields that map to email, first_name, last_name)
    const requiredFields: string[] = [];
    const optionalFields: string[] = [];
    
    Object.entries(template.field_mappings).forEach(([source, target]) => {
      if (target === 'email' || target === 'first_name' || target === 'last_name') {
        if (!requiredFields.includes(target)) {
          requiredFields.push(target);
        }
      } else {
        optionalFields.push(target);
      }
    });

    // Convert field_mappings to FieldMappingRule format
    const fieldMappings: FieldMappingRule[] = Object.entries(template.field_mappings).map(([source, target]) => ({
      source_field: source,
      target_field: target,
      required: target === 'email' || target === 'first_name' || target === 'last_name'
    }));

    // Determine niche from vertical or category
    const niche = template.vertical || template.integration_type || 'general';

    return {
      name: template.name,
      description: template.description,
      niche: niche,
      field_mappings: fieldMappings,
      required_fields: requiredFields.length > 0 ? requiredFields : ['email'],
      optional_fields: optionalFields,
      validation_rules: {
        email_format: true,
        phone_format: 'us'
      },
      sample_data: template.sample_payload || {}
    };
  };

  // Load webhook templates from API
  useEffect(() => {
    const loadWebhookTemplates = async () => {
      try {
        const response = await api.automation.getWebhookTemplates();
        const groups: WebhookTemplateGroup[] = response.data || [];
        setWebhookTemplates(groups);
        
        // Convert all webhook templates to presets
        const templatePresets: MappingPresetTemplate[] = [];
        groups.forEach(group => {
          group.templates.forEach(template => {
            templatePresets.push(convertWebhookTemplateToPreset(template));
          });
        });
        
        // Merge with system presets (system presets first, then templates)
        setPresets([...systemPresets, ...templatePresets]);
      } catch (error) {
        console.error('Error loading webhook templates:', error);
        // Fallback to system presets only
        setPresets(systemPresets);
      }
    };

    loadWebhookTemplates();
  }, []);

  // Sync customMapping prop to local state when it changes
  useEffect(() => {
    setCustomMappings(customMapping);
  }, [customMapping]);

  // Sync useCustomMapping state when props change
  useEffect(() => {
    const hasCustomMapping = Object.keys(customMapping).length > 0;
    const hasDatabasePreset = !!selectedDatabasePresetId;
    setUseCustomMapping(hasCustomMapping && !hasDatabasePreset);
  }, [customMapping, selectedDatabasePresetId]);

  const niches = [...new Set(systemPresets.map(preset => preset.niche))];

  const filteredPresets = selectedNiche 
    ? presets.filter(preset => preset.niche === selectedNiche)
    : presets;

  const handlePresetSelect = (preset: MappingPresetTemplate) => {
    // System template selected - convert to custom_mapping
    onPresetSelect(preset);
    setUseCustomMapping(true);
    // Update local state immediately so custom mapping shows the preset's mappings
    const fieldMappings: Record<string, string> = {};
    preset.field_mappings.forEach(mapping => {
      fieldMappings[mapping.source_field] = mapping.target_field;
    });
    setCustomMappings(fieldMappings);
    // Show custom mapping section so user can see and modify the mappings
    setShowCustomMapping(true);
  };

  const handleDatabasePresetSelect = (presetId: string) => {
    // Database preset selected - use mapping_preset_id instead of custom_mapping
    if (onDatabasePresetSelect) {
      onDatabasePresetSelect(presetId);
      setUseCustomMapping(false);
      // Clear custom mapping when using a database preset
      setCustomMappings({});
      setShowCustomMapping(false);
    }
  };

  const handleCustomMappingChange = (sourceField: string, targetField: string) => {
    const newMappings = { ...customMappings, [sourceField]: targetField };
    setCustomMappings(newMappings);
    if (onCustomMapping) {
      onCustomMapping(newMappings);
    }
  };

  const handleAddCustomMapping = () => {
    const newMappings = { ...customMappings, '': '' };
    setCustomMappings(newMappings);
  };

  const handleRemoveCustomMapping = (sourceField: string) => {
    const newMappings = { ...customMappings };
    delete newMappings[sourceField];
    setCustomMappings(newMappings);
    if (onCustomMapping) {
      onCustomMapping(newMappings);
    }
  };

  const renderPresetCard = (preset: MappingPresetTemplate) => (
    <div
      key={preset.name}
      className={`border rounded-lg p-4 cursor-pointer transition-colors ${
        selectedPreset?.name === preset.name
          ? 'border-blue-500 bg-blue-50'
          : 'border-gray-200 hover:border-gray-300'
      }`}
      onClick={() => handlePresetSelect(preset)}
    >
      <div className="flex items-center justify-between mb-2">
        <h3 className="font-medium text-gray-900">{preset.name}</h3>
        <span className="text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded">
          {preset.niche}
        </span>
      </div>
      <p className="text-sm text-gray-600 mb-3">{preset.description}</p>
      
      <div className="space-y-2">
        <div>
          <h4 className="text-xs font-medium text-gray-700 mb-1">Required Fields:</h4>
          <div className="flex flex-wrap gap-1">
            {preset.required_fields.map(field => (
              <span key={field} className="text-xs bg-red-100 text-red-700 px-2 py-1 rounded">
                {field}
              </span>
            ))}
          </div>
        </div>
        
        <div>
          <h4 className="text-xs font-medium text-gray-700 mb-1">Field Mappings:</h4>
          <div className="text-xs text-gray-600">
            {preset.field_mappings.slice(0, 3).map(mapping => (
              <div key={mapping.source_field} className="flex justify-between">
                <span>{mapping.source_field}</span>
                <span className="text-gray-400">→</span>
                <span>{mapping.target_field}</span>
              </div>
            ))}
            {preset.field_mappings.length > 3 && (
              <div className="text-gray-400">+{preset.field_mappings.length - 3} more...</div>
            )}
          </div>
        </div>
      </div>
    </div>
  );

  // Convert tenant presets to display format
  const tenantPresetTemplates: MappingPresetTemplate[] = tenantPresets.map(preset => ({
    name: preset.name,
    description: preset.description || '',
    niche: preset.niche,
    field_mappings: Object.entries(preset.field_mappings || {}).map(([source, target]) => ({
      source_field: source,
      target_field: target as string,
      required: false
    })),
    required_fields: preset.required_fields || [],
    optional_fields: preset.optional_fields || [],
    validation_rules: preset.validation_rules || {},
    sample_data: {}
  }));

  const allPresets = [...filteredPresets, ...tenantPresetTemplates];
  const filteredAllPresets = selectedNiche 
    ? allPresets.filter(preset => preset.niche === selectedNiche)
    : allPresets;

  return (
    <div className="space-y-6">
      {/* Mapping Mode Toggle */}
      <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
        <label className="block text-sm font-medium text-gray-700 mb-3">
          Mapping Mode
        </label>
        <div className="flex gap-4">
          <label className="flex items-center">
            <input
              type="radio"
              name="mappingMode"
              checked={!useCustomMapping}
              onChange={() => {
                setUseCustomMapping(false);
                setShowCustomMapping(false);
                // Clear custom mapping when switching to preset mode
                setCustomMappings({});
                if (onCustomMapping) {
                  onCustomMapping({});
                }
              }}
              className="mr-2"
            />
            <span className="text-sm text-gray-700">Use Preset</span>
          </label>
          <label className="flex items-center">
            <input
              type="radio"
              name="mappingMode"
              checked={useCustomMapping}
              onChange={() => {
                setUseCustomMapping(true);
                setShowCustomMapping(true);
              }}
              className="mr-2"
            />
            <span className="text-sm text-gray-700">Use Custom Mapping</span>
          </label>
        </div>
        <p className="text-xs text-gray-500 mt-2">
          {!useCustomMapping 
            ? "Select a preset template or your saved presets. Changes to presets are saved automatically."
            : "Manually configure field mappings. This will override any preset selection."}
        </p>
      </div>

      {!useCustomMapping && (
        <>
          {/* Niche Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Filter by Industry
            </label>
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                onClick={() => setSelectedNiche('')}
                className={`px-3 py-1 text-sm rounded-full border ${
                  selectedNiche === ''
                    ? 'bg-blue-100 border-blue-300 text-blue-700'
                    : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
                }`}
              >
                All
              </button>
              {niches.map(niche => (
                <button
                  key={niche}
                  type="button"
                  onClick={() => setSelectedNiche(niche)}
                  className={`px-3 py-1 text-sm rounded-full border capitalize ${
                    selectedNiche === niche
                      ? 'bg-blue-100 border-blue-300 text-blue-700'
                      : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  {niche}
                </button>
              ))}
            </div>
          </div>

          {/* Tenant Presets Section */}
          {tenantPresets.length > 0 && (
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">Your Saved Presets</h3>
              <p className="text-sm text-gray-600 mb-4">Presets you've created and saved</p>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {tenantPresets.map(preset => (
                  <div
                    key={preset.id}
                    className={`border rounded-lg p-4 cursor-pointer transition-colors ${
                      selectedDatabasePresetId === preset.id
                        ? 'border-green-500 bg-green-50'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                    onClick={() => handleDatabasePresetSelect(preset.id)}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <h3 className="font-medium text-gray-900">{preset.name}</h3>
                      <span className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded">
                        Saved
                      </span>
                    </div>
                    {preset.description && (
                      <p className="text-sm text-gray-600 mb-2">{preset.description}</p>
                    )}
                    <span className="text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded">
                      {preset.niche}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* System Preset Templates */}
          <div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">System Preset Templates</h3>
            <p className="text-sm text-gray-600 mb-4">Quick-start templates for common industries</p>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {filteredPresets.map(renderPresetCard)}
            </div>
          </div>
        </>
      )}

      {/* Custom Mapping Option */}
      {useCustomMapping && (
        <div>
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-medium text-gray-900">Custom Mapping</h3>
            <button
              type="button"
              onClick={() => setShowCustomMapping(!showCustomMapping)}
              className="px-4 py-2 text-sm border border-gray-300 rounded-md hover:bg-gray-50"
            >
              {showCustomMapping ? 'Hide' : 'Show'} Custom Mapping
            </button>
          </div>

          {showCustomMapping && (
            <div className="border rounded-lg p-4 bg-gray-50">
              <div className="space-y-3">
                {Object.entries(customMappings).map(([sourceField, targetField]) => (
                  <div key={sourceField} className="flex items-center gap-3">
                    <input
                      type="text"
                      placeholder="Source field"
                      value={sourceField}
                      onChange={(e) => {
                        const newMappings = { ...customMappings };
                        delete newMappings[sourceField];
                        newMappings[e.target.value] = targetField;
                        setCustomMappings(newMappings);
                      }}
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-md text-sm"
                    />
                    <span className="text-gray-500">→</span>
                    <input
                      type="text"
                      placeholder="Target field"
                      value={targetField}
                      onChange={(e) => handleCustomMappingChange(sourceField, e.target.value)}
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-md text-sm"
                    />
                    <button
                      type="button"
                      onClick={() => handleRemoveCustomMapping(sourceField)}
                      className="text-red-600 hover:text-red-800"
                    >
                      Remove
                    </button>
                  </div>
                ))}
                
                <button
                  type="button"
                  onClick={handleAddCustomMapping}
                  className="w-full py-2 text-sm border-2 border-dashed border-gray-300 rounded-md text-gray-600 hover:border-gray-400 hover:text-gray-700"
                >
                  + Add Field Mapping
                </button>
              </div>

              {Object.keys(customMappings).length > 0 && (
                <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-md">
                  <p className="text-sm text-blue-800">
                    Custom mapping will override preset mappings for the same target fields.
                  </p>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Selected Preset Details */}
      {selectedPreset && (
        <div className="border rounded-lg p-4 bg-green-50 border-green-200">
          <div className="flex items-center justify-between mb-2">
            <h4 className="font-medium text-green-900">Selected System Template: {selectedPreset.name}</h4>
            <span className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded">Template</span>
          </div>
          <p className="text-sm text-green-700 mb-3">{selectedPreset.description}</p>
          
          <div className="space-y-2">
            <div>
              <h5 className="text-sm font-medium text-green-800">Sample Data Format:</h5>
              <pre className="text-xs bg-white p-2 rounded border overflow-x-auto">
                {JSON.stringify(selectedPreset.sample_data, null, 2)}
              </pre>
            </div>
          </div>
          <p className="text-xs text-green-600 mt-3">
            ⚠️ This template will be converted to custom mapping. You can modify it below.
          </p>
        </div>
      )}

      {selectedDatabasePresetId && !useCustomMapping && (
        <div className="border rounded-lg p-4 bg-blue-50 border-blue-200">
          <div className="flex items-center justify-between mb-2">
            <h4 className="font-medium text-blue-900">Selected Database Preset</h4>
            <span className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded">Saved Preset</span>
          </div>
          <p className="text-sm text-blue-700 mb-3">
            Using saved preset mapping. Changes to the preset will automatically apply to this webhook.
          </p>
          {tenantPresets.find(p => p.id === selectedDatabasePresetId) && (
            <div>
              <p className="text-sm font-medium text-blue-800">
                {tenantPresets.find(p => p.id === selectedDatabasePresetId)?.name}
              </p>
              {tenantPresets.find(p => p.id === selectedDatabasePresetId)?.description && (
                <p className="text-xs text-blue-600 mt-1">
                  {tenantPresets.find(p => p.id === selectedDatabasePresetId)?.description}
                </p>
              )}
            </div>
          )}
        </div>
      )}

      {/* Active Mapping Indicator */}
      {useCustomMapping && Object.keys(customMappings).length > 0 && (
        <div className="border rounded-lg p-3 bg-amber-50 border-amber-200">
          <div className="flex items-center gap-2">
            <span className="text-xs bg-amber-100 text-amber-700 px-2 py-1 rounded font-medium">Custom Mapping Active</span>
            <span className="text-xs text-amber-700">
              {Object.keys(customMappings).length} field mapping{Object.keys(customMappings).length !== 1 ? 's' : ''} configured
            </span>
          </div>
        </div>
      )}
    </div>
  );
};

export default AutomationPresetPicker;
